import {
  Box,
  CloseButton,
  Heading,
  Text,
  Image,
  Button,
  Flex,
  Stack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "@chakra-ui/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import QueryStateWrapper from "../../components/QueryStateWrapper";
import { usePoi } from "../../data/mazemap.api";

import { useFocusOnFeature } from "../../hooks/useFocusOnFeature";
import placeholderImg from "../../assets/event-placeholder.jpg";
import moment from "moment";
import DirectionsButton from "../../components/search/DirectionsButton";
import {
  useBooking,
  useCancelBookingMutation,
  useEndBookingMutation,
} from "../../data/occupancy.api";
import DeskCheckIn from "../../components/booking/DeskCheckIn";
import SpaceSlideShow from "../../components/booking/SpaceSlideShow";
import SpaceTags from "../../components/booking/SpaceTags";
import UploadSpaceImageModal from "../../components/booking/UploadSpaceImageModal";
import ReportFaultModal from "../../components/booking/ReportFaultModal";
import BackButton from "../../components/BackButton";
import { useContext } from "react";
import { AppContext } from "../../context/app.context";
import DirectionsButtonMazemap from "../../components/search/DirectionsButtonMazemap";

const ViewBooking = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { data, isLoading, error } = useBooking(bookingId);

  console.log("Desk Data:", data);
  const checkInEnabled =
    data?.locationViewModel.locationPreferences.checkInEnabled;

  const cancelBooking = useCancelBookingMutation(data?.deskId, bookingId);
  const endBooking = useEndBookingMutation(data?.deskId, bookingId);
  const { data: poi } = usePoi(data?.locationViewModel.poiId);
  const { lng, lat } = useFocusOnFeature(poi);
  const navigate = useNavigate();
  const { resetHistory } = useContext(AppContext);

  const {
    isOpen: checkInIsOpen,
    onOpen: onOpenCheckIn,
    onClose: onCloseCheckIn,
  } = useDisclosure();
  const {
    isOpen: uploadImageIsOpen,
    onOpen: onOpenUploadImage,
    onClose: onCloseUploadImage,
  } = useDisclosure();
  const {
    isOpen: reportFaultIsOpen,
    onOpen: onOpenReportFault,
    onClose: onCloseReportFault,
  } = useDisclosure();

  return (
    <>
      <Modal isOpen={checkInIsOpen} onClose={onCloseCheckIn}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Check In</ModalHeader>
          <ModalBody>
            <DeskCheckIn deskId={data?.deskId} />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
      {data ? (
        <UploadSpaceImageModal
          location={data.locationViewModel}
          isOpen={uploadImageIsOpen}
          onClose={onCloseUploadImage}
        />
      ) : null}
      {data ? (
        <ReportFaultModal
          bookingId={data.id}
          isOpen={reportFaultIsOpen}
          onClose={onCloseReportFault}
        />
      ) : null}
      <HybridBottomSheetDrawer>
        <QueryStateWrapper isLoading={isLoading} error={error} data={data}>
          <SpaceSlideShow images={data?.locationViewModel.images ?? []} />
          <Flex direction="column" flex={1} position="relative" p={10}>
            <BackButton />
            <Box mb={5}>
              <Heading size="md">{data?.locationViewModel.name}</Heading>
              <Text>{data?.description}</Text>
              <Text opacity={0.7} mb={2}>
                {moment(data?.startDate).format("DD/MM/YYYY HH:mm")} -{" "}
                {moment(data?.endDate).format("DD/MM/YYYY HH:mm")}
              </Text>
              <SpaceTags location={data?.locationViewModel} />
            </Box>
            <Stack spacing={10} mb={5} flex={1}>
              {moment(data?.endDate).isAfter() ? (
                <Box>
                  <Heading size="sm" mb={2}>
                    Need directions?
                  </Heading>
                  {lng && lat ? <DirectionsButton lng={lng} lat={lat} /> : null}
                  {data?.locationPoiId && (
                    <>
                      <Text>Testing for mazemap directions</Text>
                      <DirectionsButtonMazemap
                        plannonID={String(data.locationPoiId)}
                      />
                    </>
                  )}
                </Box>
              ) : null}
              <Box>
                <Heading size="sm" mb={2}>
                  Want to contribute?
                </Heading>
                <Text mb={2}>
                  If you'd like to contribute to our booking system, you can
                  upload a photo of the space and we'll add it to the gallery.
                </Text>
                <Button onClick={onOpenUploadImage}>Upload Image</Button>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>
                  Something wrong?
                </Heading>
                <Text mb={2}>
                  If you had an issue with the equipment at this space, feel
                  free to submit an issue report here.
                </Text>
                <Button onClick={onOpenReportFault}>Submit Report</Button>
              </Box>
            </Stack>
            <Flex gap={3} flexWrap="wrap" justify="end">
              {moment(data?.startDate).isBefore() &&
              moment(data?.endDate).isAfter() ? (
                <>
                  <Button
                    isLoading={endBooking.isLoading}
                    onClick={async () => {
                      await endBooking.mutate();
                      navigate("/book");
                    }}
                  >
                    End Booking
                  </Button>
                  {checkInEnabled && ( // Adding a check for checkInEnabled flag
                    <Button colorScheme="green" onClick={onOpenCheckIn}>
                      Check In
                    </Button>
                  )}
                </>
              ) : moment(data?.startDate).isAfter() ? (
                <Button
                  isLoading={cancelBooking.isLoading}
                  onClick={async () => {
                    await cancelBooking.mutate();
                    resetHistory();
                    navigate("/book");
                  }}
                >
                  Cancel Booking
                </Button>
              ) : null}
            </Flex>
          </Flex>
        </QueryStateWrapper>
      </HybridBottomSheetDrawer>
    </>
  );
};

export default ViewBooking;
